// handleStreamRevenueUpdated.js
'use strict';
import throttle from 'lodash/throttle';

import { MERGE_LIVE_STREAM_DATA } from '../ActionTypes.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import { liveStreamSessionLeaderboardCategory } from '../resource/liveStreamConstants.js';

const LEADERBOARD_FETCH_THROTTLE_MILLISECONDS = 2500;
// Random per-client phase offset so clients receiving the same pusher
// event don't hit the leaderboard API at the same instant.
const LEADERBOARD_FETCH_JITTER_MILLISECONDS = 500;

const throttledFetchFunctionMap = {};

/**
 * Handle stream revenue
 * @kind action
 * @param {object} {data} - revenue updated data.
 * @param {string} {data['streamId']} - stream id.
 * @param {number} {data['total.diamonds.views']} - view diamond count.
 * @param {number} {data['total.diamonds.gifts']} - gift diamond count.
 * @param {number} {data['total.diamonds.karaoke']} - karaoke diamond count.
 * @param {number} {data['total.diamonds.chat']} - chat diamond count.
 * @param {number} {data['total.diamonds.show']} - show diamond count.
 * @param {number} {data['total.diamonds.exclusive']} - exclusive diamond count.
 * @param {number} {data['timestamp']} - unix timestamp.
 * @return {Promise} Action promise.
 */
const handleStreamRevenueUpdated =
  ({ streamId, timestamp, ...restData }) =>
  async (dispatch, getState) => {
    if (!throttledFetchFunctionMap[streamId]) {
      throttledFetchFunctionMap[streamId] = throttle(
        async unixTimestamp => {
          const fetchLiveStreamSessionLeaderboard = (
            await import('../action/fetchLiveStreamSessionLeaderboard.js')
          ).default;
          const sessionId = getLiveStreamData(
            getState(),
            [streamId],
            'sessionId'
          );
          // The session may already be reset by the time the trailing edge
          // fires (e.g. a late revenue event recreated the throttle after
          // the user left the stream).
          if (!sessionId) return;
          dispatch(
            fetchLiveStreamSessionLeaderboard({
              streamId,
              sessionId,
              category: liveStreamSessionLeaderboardCategory.TOTAL,
              page: 1,
              unixTimestamp,
            })
          );
        },
        LEADERBOARD_FETCH_THROTTLE_MILLISECONDS +
          Math.ceil(Math.random() * LEADERBOARD_FETCH_JITTER_MILLISECONDS),
        { leading: false, trailing: true }
      );
    }
    throttledFetchFunctionMap[streamId](timestamp);
    return dispatch({
      type: MERGE_LIVE_STREAM_DATA,
      payload: {
        selectPath: [streamId, 'diamondCount'],
        data: {
          ...restData,
        },
      },
    });
  };

/**
 * Cancel the pending trailing leaderboard fetch for a stream and drop its
 * throttle entry, so no fetch fires after the user leaves the stream.
 * @param {string} {streamId} - stream id.
 * @return {undefined}
 * @example
 * cancelThrottledLeaderboardFetch({ streamId: 'streamId' });
 */
const cancelThrottledLeaderboardFetch = ({ streamId }) => {
  throttledFetchFunctionMap[streamId]?.cancel();
  delete throttledFetchFunctionMap[streamId];
};

export { cancelThrottledLeaderboardFetch };
export default handleStreamRevenueUpdated;
